'use client';

import ArticlePagination from '@/components/articlePagination/ArticlePagination';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import LoginBox from '@/components/loginBox/LoginBox';
import NestedBlocks from '@/components/nestedBlocks/NestedBlocks';
import { type PillCardProps } from '@/components/pillCard/PillCard';
import type { TrackingInformation } from '@/helper/tracking';
import tracking, { EventCategory, EventSource } from '@/helper/tracking';
import { resolveStories } from '@/helper/utils';
import { getTrackingItemFromVTWPage, getVtwReplacementTexts } from '@/helper/vtwutils';
import { useUserStore } from '@/providers/UserStoreProvider';
import { useVtwSettings } from '@/providers/VtwSettingsProvider';
import type { UserState } from '@/stores/UserStore';
import Stage from '@/storyblok/stage/Stage';
import type {
  EntityVtwCategoryStoryblok,
  PageVtwBenefitStoryblok,
  PageVtwHomeStoryblok,
} from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react/rsc';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export type PageVtwHomeProps = {
  blok: PageVtwHomeStoryblok;
  currentBenefits: ISbStoryData<PageVtwBenefitStoryblok>[];
  pillCards: PillCardProps[];
};

const DELAY_MS = 2000; // Delay in milliseconds to send the data

const PageVtwHome: React.FC<PageVtwHomeProps> = ({ blok, currentBenefits, pillCards }) => {
  const { t } = useTranslation();
  const vtwSettings = useVtwSettings();

  const { name, setTrackingData, loggedIn } = useUserStore((state) => ({
    getUserState: state.getUserState,
    setTrackingData: state.setTrackingData,
    name: state.name,
    loggedIn: state.loggedIn,
  }));

  const stageBlok = blok?.stage ? blok?.stage[0] : undefined;

  const vtwReplacementTexts = useMemo(() => {
    return getVtwReplacementTexts({ loggedIn, name } as UserState);
  }, [loggedIn, name]);

  // Buffer to hold tracking information until the delay is over
  const trackingBuffer = useRef<TrackingInformation[]>([]);
  const trackingTimeout = useRef<NodeJS.Timeout | null>(null);

  // Set to keep track of already tracked items
  const trackedItemIds = useRef<Set<string>>(new Set());

  const flushTracking = () => {
    if (trackingBuffer.current.length > 0) {
      tracking.itemListViewImpression(trackingBuffer.current);

      // Add the tracked items' IDs to the set to avoid double tracking
      trackingBuffer.current.forEach((item) => trackedItemIds.current.add(item.itemId));

      trackingBuffer.current = []; // Clear buffer after firing
    }

    if (trackingTimeout.current) {
      clearTimeout(trackingTimeout.current);
      trackingTimeout.current = null;
    }
  };

  const onIntersect = (index: number) => {
    const article = currentBenefits[index]?.content;

    const trackingItem = getTrackingItemFromVTWPage(article, EventSource.VorteilsweltCurrentOffers, index);

    // Check if the item has already been tracked
    if (trackedItemIds.current.has(trackingItem.itemId)) return;

    // Add tracking item to buffer
    trackingBuffer.current.push(trackingItem);

    // If there's no active timeout, set a new one to flush the tracking after the delay
    if (!trackingTimeout.current) {
      trackingTimeout.current = setTimeout(flushTracking, DELAY_MS);
    }
  };

  // Cleanup the timeout if the component unmounts
  useEffect(() => {
    return () => {
      if (trackingTimeout.current) {
        clearTimeout(trackingTimeout.current);
      }
    };
  }, []);

  const onItemClick = (index: number) => {
    const article = currentBenefits[index]?.content;
    const categories = resolveStories<EntityVtwCategoryStoryblok>(article?.category);

    const trackingItem: TrackingInformation = {
      index,
      itemName: article?.headline.toLowerCase() ?? '',
      itemId: article?._uid ?? '',
      itemCategory: EventCategory.Vorteilswelt,
      itemCategory2: categories && (categories[0]?.content.name ?? ''),
      itemListName: EventSource.VorteilsweltCurrentOffers,
    };
    setTrackingData(trackingItem);
    tracking.nonCommodityOrBenefitSelect([trackingItem]);
  };

  return (
    <div data-testid="vtw-home-page">
      {stageBlok && <Stage blok={stageBlok} />}

      <div className="grid-wrapper">
        <div className="content-bloks-wrapper">
          <LoginBox
            loggedInText={vtwSettings?.logged_in_text}
            loggedOutText={vtwSettings?.logged_out_text}
            replacements={vtwReplacementTexts}
          />

          {currentBenefits && currentBenefits.length > 0 && (
            <section className="article-more">
              <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H3}>
                {t('vtw.currentBenefits')}
              </HeadlineField>
              <ArticlePagination
                baseUrl="/vorteilswelt/"
                articles={pillCards}
                onIntersect={onIntersect}
                onItemClick={onItemClick}
              />
            </section>
          )}

          <NestedBlocks blocks={blok?.content} />
        </div>
      </div>
    </div>
  );
};

export default PageVtwHome;
